import { useTranslations } from 'next-intl';
import { useRef, useEffect } from 'react';
import type { MouseEvent, ReactNode } from 'react';

import { useToolContext } from '@/context/ToolContext';

interface ItemMenuProps {
  children: ReactNode;
}

const ItemMenu = ({ children }: ItemMenuProps) => {
  const menuItemNames = ['Items', 'Lines', 'Tools', 'Fields'];

  const t = useTranslations();

  const ToolContextState = useToolContext();
  const itemsRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLUListElement>(null);

  let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

  const setNavItemName = (type: string) => {
    switch (type) {
      case 'Items': {
        return t('DRWNG__MENU_PLAYERS');
      }
      case 'Lines': {
        return t('DRWNG__MENU_ARROWS');
      }
      case 'Tools': {
        return t('DRWNG__MENU_TOOLS');
      }
      case 'Fields': {
        return t('DRWNG__MENU_FIELDS');
      }
    }
  };

  // eslint-disable-next-line unicorn/consistent-function-scoping -- kept inside the component alongside the other menu handlers for cohesion
  const setActive = (name: string) => {
    document.querySelector(`#${name}-container`)!.scrollIntoView({
      inline: 'start',
      behavior: 'smooth',
      block: 'nearest',
    });
  };

  const hideMenu = (e: MouseEvent<HTMLDivElement>) => {
    if (!menuRef.current!.contains(e.target as Node)) {
      ToolContextState.setIsMenuActive(false);
    }
  };

  const handleScroll = () => {
    ToolContextState.setPreventClickBeforeScroll(true);

    let current = '';

    for (const section of itemsRef.current!.children) {
      const sectionOffset = (section as HTMLElement).offsetLeft - 100;
      if (itemsRef.current!.scrollLeft >= sectionOffset) {
        current = section.getAttribute('id') ?? '';
      }
    }

    // Get everything before -container from the ID to match the header item name
    const regPattern = /^(.*?)-/;
    const splitID = regPattern.exec(current)![1];

    for (const a of navRef.current!.children) {
      a.classList.remove('Active-menuItem');
      if (a.id === splitID) {
        a.classList.add('Active-menuItem');
      }
    }

    if (scrollTimeout) clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      ToolContextState.setPreventClickBeforeScroll(false);
    }, 100);
  };

  useEffect(() => {
    if (!ToolContextState.isMobileStateRef.current) {
      return;
    }

    // eslint-disable-next-line unicorn/prefer-observer-apis -- horizontal scroll-spy needs the live scrollLeft/offsetLeft; IntersectionObserver reports visibility, not scroll offset, so it is not equivalent
    itemsRef.current!.addEventListener('scroll', handleScroll, {
      capture: false,
    });
    return () => {
      itemsRef.current!.removeEventListener('scroll', handleScroll, false);
    };
  }, [ToolContextState.isMobileStateRef.current]);

  return (
    <>
      <div
        ref={menuRef}
        className={`ItemMenu ${
          ToolContextState?.isMenuActive && !ToolContextState?.selection
            ? 'ItemMenu-showMenu'
            : ''
        }`}
      >
        <nav className="ItemMenu-navigation">
          <ul ref={navRef}>
            {menuItemNames.map((name, i) => {
              return (
                <li
                  key={`${name}--${i}`}
                  id={name}
                  className={name === 'Items' ? 'Active-menuItem' : ''}
                >
                  <a
                    href={`#${name}-container`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActive(name);
                    }}
                    aria-label={setNavItemName(name)}
                  >
                    {setNavItemName(name)}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
        <div ref={itemsRef} className="ItemMenu-inner">
          {children}
        </div>
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click-to-dismiss backdrop; the menu also closes via its own controls, so this stays a pointer-only convenience rather than a redundant focusable element */}
      <div
        className={
          ToolContextState.isMenuActive
            ? 'ItemMenu-transparentBackground'
            : 'ItemMenu-hidden'
        }
        onClick={hideMenu}
      />
    </>
  );
};

export default ItemMenu;
